import { CONFIGURATOR_ID, SUBFAMILY_ID } from '@/constants/ui.constants'
import { action, createRequestTypes } from '@/utils/actions.utils'
import { errorActions } from '@/redux/actions'
import { userService } from '@/services'
import Toggles from '@domain/toggles/Toggles'
import { CONFIGURATOR_TYPES } from '@domain/data/Constants'
import { PRIVILEGES } from '@/constants'
import { applyPlaceOrderPrivilege } from '@/utils/placeOrderButton.utils'
import { buildProjectDetailsButtons } from '@/utils/projectDetailsButtons.utils'
import { SITE_LOCATIONS } from '@/constants/siteLocations.constants'

export const NAVIGATE_TO = 'NAVIGATE_TO'
const navigateTo = ({ path, requiredUserStatus = null, state = {} }) => {
	return action(NAVIGATE_TO, { path, requiredUserStatus, state })
}

export const NAVIGATE_TO_CONFIGURATOR = 'NAVIGATE_TO_CONFIGURATOR'
const navigateToConfigurator = ({ configuratorType, preconfigId, projectId, state, subFamilyId, activeTab, replace = false }) => {
	return (dispatch) => {
		const projectUrlPart = projectId ? `/project/${projectId}/add-product` : '/new-product'
		const preconfigIdUrlPart = preconfigId ? `?preconfigId=${preconfigId}` : ''

		switch (configuratorType.id) {
			case CONFIGURATOR_ID.CREA:
				return dispatch(
					action(NAVIGATE_TO, {
						path: `${projectUrlPart}/crea-configurator${preconfigIdUrlPart}`,
						state
					})
				)
			case CONFIGURATOR_ID.DRAWING:
				switch (subFamilyId) {
					case SUBFAMILY_ID.PLUSMINUS:
						return dispatch(
							action(NAVIGATE_TO, {
								path: `${projectUrlPart}/plusminus-drawing-configurator${preconfigIdUrlPart}`,
								state
							})
						)
					case SUBFAMILY_ID.STICKS:
						return dispatch(
							action(NAVIGATE_TO, {
								path: `${projectUrlPart}/sticks-drawing-configurator${preconfigIdUrlPart}`,
								state
							})
						)
					default:
						throw 'SubfamilyId does not match any drawing configurator'
				}
			case CONFIGURATOR_ID.NODRAWING:
				switch (subFamilyId) {
					case SUBFAMILY_ID.PLUSMINUS:
						return dispatch(
							action(NAVIGATE_TO, {
								path: `${projectUrlPart}/plusminus-nodrawing-configurator`,
								state
							})
						)
					case SUBFAMILY_ID.STICKS:
						return dispatch(
							action(NAVIGATE_TO, {
								path: `${projectUrlPart}/sticks-nodrawing-configurator`,
								state
							})
						)
					default:
						throw 'SubfamilyId does not match any not-drawing configurator'
				}
			case CONFIGURATOR_TYPES.MINI_COMPOSITOR:
			case CONFIGURATOR_TYPES.PIECES_LIST:
			case CONFIGURATOR_TYPES.COMPOSITOR_2D:
				const { applicationLocation, subFamily, catalog, trackSystemFilter, family, familyId: familyIdFromProduct, model, createdFrom } = state.product || {};
				const queryParams = new URLSearchParams();
				if (subFamily) queryParams.append('subFamily', subFamily);
				if (applicationLocation) queryParams.append('applicationLocation', applicationLocation);
				if (catalog) queryParams.append('catalog', catalog);
				if (activeTab) queryParams.append('activeTab', activeTab);
				if (trackSystemFilter) queryParams.append('trackSystemFilter', trackSystemFilter === 'track');
				const resolvedFamilyId = familyIdFromProduct || family?.id;
				if (resolvedFamilyId) queryParams.append('familyId', resolvedFamilyId);
				if (model) queryParams.append('model', model);
				if (createdFrom) queryParams.append('createdFrom', createdFrom);
				// Carried from the "Create new project" entry point through /create-project.
				// Read off the live URL rather than threaded as an argument: the intent has to
				// survive the collection-selection screens in between, and the URL is the only
				// thing that does (react-router state does not survive a refresh).
				const carriedIntent = new URLSearchParams(window.location.search).get('intent');
				if (carriedIntent === 'new-project') queryParams.append('intent', carriedIntent);
				const PATH_DICTIONARY = {
					[CONFIGURATOR_TYPES.MINI_COMPOSITOR]: `${projectUrlPart}/RegularConfigurator`,
					[CONFIGURATOR_TYPES.PIECES_LIST]: `${projectUrlPart}/BundleListConfigurator`,
					[CONFIGURATOR_TYPES.COMPOSITOR_2D]: `${projectUrlPart}/ToolkitListConfigurator`,
				}
				const queryString = queryParams.toString();
				const basePath = PATH_DICTIONARY[configuratorType.id];
				const path = `${basePath}?${queryString}`;

				return dispatch(action(NAVIGATE_TO, { path, replace }))
			default:
				dispatch(errorActions.setError('No configurator setted!'))
		}
	}
}

export const NAVIGATION_END = 'NAVIGATION_END'
const navigationEnd = () => {
	return action(NAVIGATION_END)
}

export const SET_NAVIGATION = 'SET_NAVIGATION'
const setNavigation = (payload) => {
	return action(SET_NAVIGATION, payload)
}

export const SET_ACTION_DONE = 'SET_ACTION_DONE'
const setActionDone = (actionDone) => {
	return (dispatch) => {
		dispatch(action(SET_ACTION_DONE, actionDone))
		return dispatch(resetActionDone())
	}
}

export const RESET_ACTION_DONE = 'RESET_ACTION_DONE'
const resetActionDone = () => {
	return action(RESET_ACTION_DONE)
}

export const GET_USER_CONFIGURATIONS_BY_SITE_LOCATION = createRequestTypes('GET_USER_CONFIGURATIONS_BY_SITE_LOCATION')
// getUserByCrmIdV3: project detail buttons come from the v3 privileges, not from masters' role table
const fetchUserConfigurations = (siteLocation, account) => {
	if (Toggles.isEnabled('getUserByCrmIdV3') && siteLocation === SITE_LOCATIONS.PROJECT_DETAILS) {
		return Promise.resolve({ data: { BUTTONS: buildProjectDetailsButtons(account), SELECTS: {} } })
	}
	return userService.userConfigurationsBySiteLocation(siteLocation)
}
const userConfigurationsBySiteLocation = (siteLocation) => {
	return async (dispatch, getState) => {
		try {
			await dispatch(request({ siteLocation }))
			const res = await fetchUserConfigurations(siteLocation, getState()?.auth?.account)
			let userButtons = res.data.BUTTONS;

			if (Toggles.isEnabled('hideCloneProject')) {
				Object.keys(userButtons).forEach(category => {
					userButtons[category] = userButtons[category].filter(button => button.key !== 'button.project.duplicate');
				});
			}

			if (Toggles.isEnabled('getUserByCrmIdV3')) {
				res.data.BUTTONS = applyPlaceOrderPrivilege(userButtons, siteLocation, getState()?.auth?.account)
			}

			return dispatch(success({ siteLocation, userButtons: res.data }))
		} catch (error) {
			return dispatch(failure(error, { siteLocation }))
		}
	}

	function request() {
		return action(GET_USER_CONFIGURATIONS_BY_SITE_LOCATION.REQUEST, { siteLocation })
	}
	function success(payload) {
		return action(GET_USER_CONFIGURATIONS_BY_SITE_LOCATION.SUCCESS, payload)
	}
	function failure(error, payload) {
		return action(GET_USER_CONFIGURATIONS_BY_SITE_LOCATION.FAILURE, payload, error)
	}
}

export const FETCH_SUBMENU = createRequestTypes('FETCH_SUBMENU')
const fetchSubmenuLinks = ({ catalogId, role, user }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			const res = await userService.fetchSubmenuLinks({ catalogId, role })
			const toggleEnabled = Toggles.isEnabled("restrictOrdersAndInvoicesByPrivilegie")
			if (toggleEnabled && user) {
				const filteredLinks = res.data.filter((link) => {
					if (link.key == 'menu.orders' && !user.privileges?.includes(PRIVILEGES.ORDER_VIEW)) {
						return false
					}
					if (link.key == 'menu.invoices' && !user.privileges?.includes(PRIVILEGES.INVOICE_VIEW)) {
						return false
					}
					return true
				})
				res.data = filteredLinks
			}
			return dispatch(success(res.data))
		} catch (error) {
			return dispatch(failure(error))
		}
	}
	function request(payload) {
		return action(FETCH_SUBMENU.REQUEST, payload)
	}
	function success(payload) {
		return action(FETCH_SUBMENU.SUCCESS, payload)
	}
	function failure(err) {
		return action(FETCH_SUBMENU.FAILURE, null, err)
	}
}

export const HIDE_NOTIFICATION = 'HIDE_NOTIFICATION'
const dismissNotification = (notificationId) => {
	return action(HIDE_NOTIFICATION, notificationId)
}

export const SHOW_NOTIFICATION = 'SHOW_NOTIFICATION'
const showNotification = (notificationId) => {
	return action(SHOW_NOTIFICATION, notificationId)
}

export const COLLECTION_VIEW = 'COLLECTION_VIEW'
const collectionView = (payload) => {
	return action(COLLECTION_VIEW, payload)
}

export default {
	navigateTo,
	navigateToConfigurator,
	navigationEnd,
	setNavigation,
	setActionDone,
	resetActionDone,
	userConfigurationsBySiteLocation,
	fetchSubmenuLinks,
	dismissNotification,
	showNotification,
	collectionView
}
